import type { FieldType, MapsTo, Operator } from '@re/form-engine';
import { isEn, tr, uiLocale } from './i18n';

export const ORG_STATUS: Record<string, { label: string; color: 'success' | 'warning' | 'default' }> = {
  ACTIVE: { label: tr('نشطة', 'Active'), color: 'success' },
  SUSPENDED: { label: tr('معلّقة', 'Suspended'), color: 'warning' },
  ARCHIVED: { label: tr('مؤرشفة', 'Archived'), color: 'default' },
};

export const PROPERTY_STATUS: Record<string, string> = {
  PENDING_REVIEW: tr('بانتظار المراجعة', 'Pending review'),
  AVAILABLE: tr('متاح', 'Available'),
  RESERVED: tr('محجوز', 'Reserved'),
  SOLD: tr('مباع', 'Sold'),
  RENTED: tr('مؤجر', 'Rented'),
  WITHDRAWN: tr('مسحوب', 'Withdrawn'),
  ARCHIVED: tr('مؤرشف', 'Archived'),
};

export const REQUEST_STATUS: Record<string, string> = {
  NEW: tr('جديد', 'New'),
  ASSIGNED: tr('معيّن', 'Assigned'),
  IN_PROGRESS: tr('قيد المتابعة', 'In progress'),
  CONTACTED: tr('تم التواصل', 'Contacted'),
  MATCHED: tr('تمت المطابقة', 'Matched'),
  WON: tr('ناجح', 'Won'),
  LOST: tr('خاسر', 'Lost'),
  CANCELLED: tr('ملغى', 'Cancelled'),
};

export const SOURCE: Record<string, string> = { INTERNAL: tr('داخلي', 'Internal'), PUBLIC_LINK: tr('رابط عام', 'Public link') };
export const DIRECTION: Record<string, string> = { OFFER: tr('عرض ← العقارات', 'Offer → Properties'), DEMAND: tr('طلب ← الطلبات', 'Demand → Requests'), SERVICE: tr('خدمة ← الطلبات (خدماتنا)', 'Service → Requests (our services)') };
export const FIELD_TYPE: Record<FieldType, string> = {
  text: tr('نص قصير', 'Short text'),
  textarea: tr('نص طويل', 'Long text'),
  number: tr('رقم', 'Number'),
  number_range: tr('نطاق رقمي (من – إلى)', 'Number range (from – to)'),
  currency: tr('مبلغ', 'Amount'),
  phone: tr('رقم هاتف', 'Phone number'),
  email: tr('بريد إلكتروني', 'Email'),
  date: tr('تاريخ', 'Date'),
  date_range: tr('نطاق تاريخ', 'Date range'),
  boolean: tr('نعم / لا', 'Yes / No'),
  radio: tr('اختيار واحد (أزرار)', 'Single choice (buttons)'),
  dropdown: tr('اختيار من قائمة', 'Dropdown'),
  checkbox_group: tr('اختيار متعدد', 'Multiple choice'),
  request_type: tr('نوع الطلب', 'Request type'),
  property_category: tr('فئة العقار', 'Property category'),
  property_type: tr('نوع العقار', 'Property type'),
  property_features: tr('مميزات العقار', 'Property features'),
  location: tr('الموقع (دولة ← حي)', 'Location (country → neighborhood)'),
  map_point: tr('موقع على الخريطة', 'Map location'),
  image_upload: tr('رفع صور', 'Image upload'),
  file_upload: tr('رفع ملفات', 'File upload'),
  section: tr('عنوان قسم', 'Section heading'),
  info: tr('نص إرشادي', 'Info text'),
};

export const MAPS_TO_LABEL: Record<MapsTo, string> = {
  request_type: tr('نوع الطلب', 'Request type'),
  category: tr('الفئة', 'Category'),
  property_type: tr('نوع العقار', 'Property type'),
  features: tr('المميزات', 'Features'),
  location: tr('الموقع', 'Location'),
  coordinates: tr('الموقع على الخريطة (عرض)', 'Map location (offer)'),
  price: tr('السعر', 'Price'),
  price_range: tr('الميزانية (من – إلى)', 'Budget (from – to)'),
  area: tr('المساحة', 'Area'),
  area_range: tr('المساحة (من – إلى)', 'Area (from – to)'),
  building_area: tr('مساحة البناء', 'Building area'),
  land_area: tr('مساحة الأرض', 'Land area'),
  outer_area: tr('المساحة الخارجية (عرض)', 'Outer area (offer)'),
  inner_area_range: tr('المساحة الداخلية', 'Inner area'),
  outer_area_range: tr('المساحة الخارجية', 'Outer area'),
  land_area_range: tr('مساحة الأرض (من – إلى)', 'Land area (from – to)'),
  building_area_range: tr('مساحة البناء (من – إلى)', 'Building area (from – to)'),
  rooms: tr('عدد الغرف', 'Rooms'),
  bathrooms: tr('عدد الحمامات', 'Bathrooms'),
  floor: tr('الطابق', 'Floor'),
  contact_name: tr('اسم العميل', 'Client name'),
  contact_phone: tr('هاتف العميل', 'Client phone'),
  contact_email: tr('بريد العميل', 'Client email'),
  images: tr('الصور', 'Images'),
  notes: tr('ملاحظات', 'Notes'),
};

export const OPERATOR_LABEL: Record<Operator, string> = {
  eq: tr('يساوي', 'Equals'),
  neq: tr('لا يساوي', 'Does not equal'),
  in: tr('ضمن', 'Is one of'),
  not_in: tr('ليس ضمن', 'Is not one of'),
  contains: tr('يحتوي', 'Contains'),
  contains_any: tr('يحتوي أياً من', 'Contains any of'),
  contains_all: tr('يحتوي كل', 'Contains all of'),
  not_contains: tr('لا يحتوي', 'Does not contain'),
  gt: tr('أكبر من', 'Greater than'),
  gte: tr('أكبر من أو يساوي', 'Greater than or equal to'),
  lt: tr('أصغر من', 'Less than'),
  lte: tr('أصغر من أو يساوي', 'Less than or equal to'),
  between: tr('بين', 'Between'),
  is_empty: tr('فارغ', 'Is empty'),
  is_not_empty: tr('غير فارغ', 'Is not empty'),
  location_in: tr('ضمن الموقع', 'Within location'),
};

export const fmtDate = (d: string | null | undefined) => (d ? new Date(d).toLocaleString(uiLocale(), { dateStyle: 'medium', timeStyle: 'short' }) : '—');
export const fmtDay = (d: string | null | undefined) => (d ? new Date(d).toLocaleDateString(uiLocale(), { dateStyle: 'medium' }) : '—');
export const fmtNum = (n: string | number | null | undefined) => (n === null || n === undefined || n === '' ? '—' : Number(n).toLocaleString(uiLocale()));

// ---------------------------------------------------------------------------
// Subscriptions & pulse
// ---------------------------------------------------------------------------
export const SUBSCRIPTION_STATUS: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  ACTIVE: { label: tr('فعّال', 'Active'), color: 'success' },
  EXPIRING: { label: tr('ينتهي قريباً', 'Expiring soon'), color: 'warning' },
  EXPIRED: { label: tr('منتهي', 'Expired'), color: 'error' },
  NONE: { label: tr('بدون اشتراك', 'No subscription'), color: 'default' },
};

export const HEALTH: Record<string, { label: string; hint: string; color: string }> = {
  ACTIVE: { label: tr('نشطة', 'Active'), hint: tr('دخول خلال 7 أيام وفيها حركة خلال 30 يوماً', 'Signed in within 7 days with activity in the last 30 days'), color: '#3d5a46' },
  COOLING: { label: tr('تبرد', 'Cooling'), hint: tr('حركتها خفّت — تابعها قبل أن تنام', 'Activity is dropping — follow up before it goes dormant'), color: '#a67c2d' },
  SLEEPING: { label: tr('نائمة', 'Dormant'), hint: tr('لا دخول منذ أسبوعين ولا حركة خلال 30 يوماً — اتصل بها', 'No sign-in for two weeks and no activity in 30 days — reach out'), color: '#6e4542' },
  NEW: { label: tr('جديدة', 'New'), hint: tr('أُنشئت خلال أسبوع ولم يدخلها أحد بعد', 'Created this week; no one has signed in yet'), color: '#5c5c5c' },
};

export const PAYMENT_METHOD: Record<string, string> = { CASH: tr('نقداً', 'Cash'), TRANSFER: tr('حوالة بنكية', 'Bank transfer'), CLIQ: tr('كليك', 'CliQ'), OTHER: tr('أخرى', 'Other') };

export const fmtMoney = (n: string | number | null | undefined, currency = 'JOD') =>
  n === null || n === undefined || n === '' ? '—' : `${Number(n).toLocaleString(uiLocale(), { maximumFractionDigits: 2 })} ${currency === 'JOD' ? tr('د.أ', 'JOD') : currency}`;

export const fmtDuration = (count: number, unit: 'DAY' | 'MONTH') => {
  if (isEn()) {
    if (unit === 'MONTH') return count === 12 ? '1 year' : count === 1 ? '1 month' : `${count} months`;
    return count === 1 ? '1 day' : `${count} days`;
  }
  if (unit === 'MONTH') return count === 12 ? 'سنة' : count === 1 ? 'شهر' : count === 2 ? 'شهران' : `${count} أشهر`;
  return count === 1 ? 'يوم' : count === 2 ? 'يومان' : count <= 10 ? `${count} أيام` : `${count} يوماً`;
};

/** "باقي 5 أيام" / "انتهى منذ 3 أيام" / "ينتهي اليوم" */
export const fmtDaysLeft = (d: number | null) => {
  if (d === null) return '—';
  if (d === 0) return tr('ينتهي اليوم', 'Ends today');
  const n = Math.abs(d);
  if (isEn()) {
    const daysEn = n === 1 ? '1 day' : `${n} days`;
    return d > 0 ? `${daysEn} left` : `Expired ${daysEn} ago`;
  }
  const days = n === 1 ? 'يوم' : n === 2 ? 'يومين' : n <= 10 ? `${n} أيام` : `${n} يوماً`;
  return d > 0 ? `باقي ${days}` : `انتهى منذ ${days}`;
};

export const fmtAgo = (d: string | null | undefined) => {
  if (!d) return tr('لم يدخل أحد', 'Never signed in');
  const days = Math.floor((Date.now() - new Date(d).getTime()) / 86_400_000);
  if (days <= 0) return tr('اليوم', 'Today');
  if (days === 1) return tr('أمس', 'Yesterday');
  return tr(`منذ ${days <= 10 ? `${days} أيام` : `${days} يوماً`}`, `${days} days ago`);
};
